import { useEffect, useMemo, useState } from 'react';
import {
  Search, Plus, X, AlertTriangle, MapPin, Store, Gift, Package, Truck, ScrollText, ChevronRight, Image as ImageIcon
} from 'lucide-react';
import './RepairsMobileHome.css';

// iOS WebView Tamirat ana görünümü (/mobile/tamirat → Repairs mobileApp modu).
// Liste, filtreler ve sayılar Repairs.js'in kendi hesaplarından gelir (filteredRepairs,
// deliveredRepairs, statusCounts) — web ile birebir aynı; burada yalnızca telefon yerleşimi var.
// Karta dokununca web'deki detay modalı açılır (durum değiştirme, ödeme, düzenleme orada).

const STATUS_FILTERS = [
  { key: 'all', label: 'Tümü' },
  { key: 'received', label: 'Teslim Alındı' },
  { key: 'sent_to_workshop', label: 'Tamirata Yolda' },
  { key: 'at_workshop', label: 'Atölyede' },
  { key: 'in_progress', label: 'Tamiratta' },
  { key: 'completed', label: 'Tamamlandı' },
  { key: 'returning_to_store', label: 'Mağazaya Yolda' },
  { key: 'ready_for_pickup', label: 'Tamirattan Geldi' },
  { key: 'checked', label: 'Kontrol Edildi' },
  { key: 'delivered', label: 'Teslim Edilenler' },
];

const STORE_FILTERS = [
  { key: 'all', label: 'Tümü' },
  { key: 'gold', label: 'Akik Gold' },
  { key: 'osmangazi', label: 'Osmangazi' },
  { key: 'yesilsu', label: 'Yeşil Su' },
];

const STATUS_TONE = {
  received: 'amber',
  sent_to_workshop: 'orange',
  at_workshop: 'orange',
  in_progress: 'teal',
  completed: 'green',
  returning_to_store: 'indigo',
  ready_for_pickup: 'blue',
  checked: 'blue',
  delivered: 'purple',
};

const PAGE_SIZE = 40;

const tl = (n) => `₺${Number(n || 0).toLocaleString('tr-TR', { maximumFractionDigits: 2 })}`;
const shortDate = (d) => (d ? new Date(d).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: '2-digit' }) : '');

const parseItems = (repair) => {
  let items = [];
  if (repair.items) {
    try {
      items = typeof repair.items === 'string' ? JSON.parse(repair.items) : repair.items;
    } catch (e) {
      items = [];
    }
  }
  if (!Array.isArray(items) || items.length === 0) {
    items = [{ item_description: repair.item_description, quantity: 1 }];
  }
  return items;
};

const RepairsMobileHome = ({
  loading,
  repairs,
  statusCounts,
  filterStatus, setFilterStatus,
  searchTerm, setSearchTerm,
  isAdmin,
  filterStore, setFilterStore,
  repairPoints, filterRepairPoint, setFilterRepairPoint,
  customerDebtMap,
  getStatusLabel, getStoreName,
  getPaidAmount, getCollectedAmount,
  imageHost,
  onOpenRepair, onNewRepair,
  readyCount, workshopCount, receivedCount,
  onDeliver, onWorkshop, onBulkDispatch, onOpenLog,
}) => {
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  // Filtre/arama değişince liste başa dönsün
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [filterStatus, searchTerm, filterStore, filterRepairPoint]);

  // Özet: açık tamiratlardaki tahsil edilmemiş tutar (fiyatı belli olanlar)
  const openBalance = useMemo(() => repairs.reduce((sum, r) => {
    if (r.status === 'delivered' || r.price_type !== 'normal') return sum;
    const remaining = Number(r.estimated_price || 0) - Number(r.deposit_amount || 0) - getPaidAmount(r);
    return remaining > 0.01 ? sum + remaining : sum;
  }, 0), [repairs, getPaidAmount]);

  const reminderHours = parseInt(localStorage.getItem('repairReminderHours') || '24', 10);
  const visible = repairs.slice(0, visibleCount);
  const isDeliveredTab = filterStatus === 'delivered';

  const actions = [
    { key: 'deliver', label: 'Ürün Teslim Al', icon: <Gift size={18} />, count: readyCount, onClick: onDeliver },
    workshopCount !== null && { key: 'workshop', label: 'Atölye Çıkış', icon: <Package size={18} />, count: workshopCount, onClick: onWorkshop },
    { key: 'bulk', label: 'Toplu Çıkış', icon: <Truck size={18} />, count: receivedCount, onClick: onBulkDispatch },
    { key: 'log', label: 'İşlem Geçmişi', icon: <ScrollText size={18} />, count: 0, onClick: onOpenLog },
  ].filter(Boolean);

  return (
    <div className="rmh">
      {/* Özet */}
      <div className="rmh-hero">
        <div className="rmh-hero-main">
          <span className="rmh-hero-label">Açık Tamirat</span>
          <span className="rmh-hero-value">{statusCounts.all || 0}</span>
        </div>
        <div className="rmh-hero-stats">
          <button
            type="button"
            className={`rmh-hero-stat${statusCounts.overdue > 0 ? ' is-alert' : ''}`}
            onClick={() => setFilterStatus(filterStatus === 'overdue' ? 'all' : 'overdue')}
          >
            <span>Geç Kalan</span>
            <strong>{statusCounts.overdue || 0}</strong>
          </button>
          <div className="rmh-hero-stat">
            <span>Tahsil Edilecek</span>
            <strong>{tl(openBalance)}</strong>
          </div>
        </div>
      </div>

      {/* Hızlı işlemler */}
      <div className="rmh-actions">
        {actions.map(a => (
          <button key={a.key} type="button" className="rmh-action" onClick={a.onClick}>
            <span className="rmh-action-icon">{a.icon}</span>
            <span className="rmh-action-label">{a.label}</span>
            {a.count > 0 && <span className="rmh-action-badge">{a.count}</span>}
          </button>
        ))}
      </div>

      {/* Arama */}
      <div className="rmh-search">
        <Search size={17} />
        <input
          type="search"
          inputMode="search"
          placeholder="Müşteri, telefon veya ürün ara"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        {searchTerm && (
          <button type="button" className="rmh-search-clear" onClick={() => setSearchTerm('')} aria-label="Temizle">
            <X size={14} />
          </button>
        )}
      </div>

      {/* Mağaza (yalnızca admin) */}
      {isAdmin && (
        <div className="rmh-segment" role="tablist">
          {STORE_FILTERS.map(s => (
            <button
              key={s.key}
              type="button"
              role="tab"
              aria-selected={filterStore === s.key}
              className={filterStore === s.key ? 'is-active' : ''}
              onClick={() => setFilterStore(s.key)}
            >
              {s.label}
            </button>
          ))}
        </div>
      )}

      {/* Durum filtreleri — sayılar o filtreye basınca listelenecek kayıt sayısıdır */}
      <div className="rmh-chips">
        {STATUS_FILTERS.map(f => (
          <button
            key={f.key}
            type="button"
            className={`rmh-chip${filterStatus === f.key ? ' is-active' : ''}`}
            onClick={() => setFilterStatus(f.key)}
          >
            {f.label}
            <span className="rmh-chip-count">{statusCounts[f.key] || 0}</span>
          </button>
        ))}
        <button
          type="button"
          className={`rmh-chip is-danger${filterStatus === 'overdue' ? ' is-active' : ''}`}
          onClick={() => setFilterStatus('overdue')}
        >
          <AlertTriangle size={13} /> Geç Teslim
          <span className="rmh-chip-count">{statusCounts.overdue || 0}</span>
        </button>
      </div>

      {/* Tamirat noktası */}
      {repairPoints.length > 0 && (
        <label className="rmh-point">
          <MapPin size={15} />
          <span>Tamirat Noktası</span>
          <select value={filterRepairPoint} onChange={(e) => setFilterRepairPoint(e.target.value === 'all' ? 'all' : Number(e.target.value))}>
            <option value="all">Tümü</option>
            {repairPoints.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <ChevronRight size={15} className="rmh-point-chevron" />
        </label>
      )}

      <div className="rmh-section-title">
        {isDeliveredTab ? 'Teslim Edilenler' : filterStatus === 'overdue' ? 'Geç Teslim Tarihi Gelenler' : 'Tamiratlar'}
        <span className="rmh-count">{repairs.length}</span>
      </div>

      {loading && repairs.length === 0 ? (
        <div className="rmh-list">
          {[0, 1, 2].map(i => <div key={i} className="rmh-card rmh-skeleton" />)}
        </div>
      ) : repairs.length === 0 ? (
        <div className="rmh-empty">
          <div className="rmh-empty-icon">🔧</div>
          <div className="rmh-empty-title">Tamirat bulunamadı</div>
          <div className="rmh-empty-text">Bu filtreye uygun kayıt yok.</div>
        </div>
      ) : (
        <div className="rmh-list">
          {visible.map(repair => {
            const isStoreRepair = repair.store_id && (!repair.customer_phone || repair.customer_phone.trim() === '');
            const items = parseItems(repair).filter(i => i.item_description?.trim());
            const itemsText = items
              .map(i => `${i.item_description}${(i.quantity || 1) > 1 ? ` ×${i.quantity}` : ''}`)
              .join(', ');
            const thumb = items.find(i => Array.isArray(i.photos) && i.photos.length > 0)?.photos[0];
            const photoCount = items.reduce((n, i) => n + (Array.isArray(i.photos) ? i.photos.length : 0), 0);

            const deadline = repair.deadline ? new Date(repair.deadline) : null;
            const hoursLeft = deadline ? (deadline - new Date()) / 36e5 : null;
            const isOverdue = deadline && repair.status !== 'delivered' && hoursLeft < 0;
            const isUrgent = deadline && repair.status !== 'delivered' && !repair.reminder_dismissed && hoursLeft <= reminderHours;

            const total = Number(repair.estimated_price || 0);
            const collected = getCollectedAmount(repair);
            const remaining = total - Number(repair.deposit_amount || 0) - getPaidAmount(repair);
            const hasDebt = !isStoreRepair && customerDebtMap[repair.customer_id] > 0;
            const effectiveStoreId = repair.transferred_to_store_id || repair.store_id;

            return (
              <button
                key={repair.id}
                type="button"
                className={`rmh-card${isOverdue ? ' is-overdue' : ''}`}
                onClick={() => onOpenRepair(repair.id)}
              >
                <div className="rmh-card-top">
                  <span className="rmh-code">#{repair.repair_code || repair.id}</span>
                  <span className={`rmh-status tone-${STATUS_TONE[repair.status] || 'gray'}`}>{getStatusLabel(repair.status)}</span>
                  {repair.repair_not_done === 1 && <span className="rmh-status tone-red">Yapılmadı</span>}
                </div>

                <div className="rmh-card-body">
                  <div className="rmh-card-text">
                    <div className="rmh-customer">
                      {isStoreRepair && <Store size={14} />}
                      <span className="rmh-customer-name">{repair.customer_name || 'İsimsiz'}</span>
                      {hasDebt && (
                        <span className="rmh-debt" title="Müşterinin borcu var">
                          <AlertTriangle size={12} /> Borçlu
                        </span>
                      )}
                    </div>
                    {!isStoreRepair && repair.customer_phone && <div className="rmh-phone">{repair.customer_phone}</div>}
                    {itemsText && <div className="rmh-items">{itemsText}</div>}
                  </div>
                  {thumb && (
                    <div className="rmh-thumb">
                      <img src={`${imageHost}${thumb}`} alt="" loading="lazy" />
                      {photoCount > 1 && <span className="rmh-thumb-count"><ImageIcon size={10} /> {photoCount}</span>}
                    </div>
                  )}
                </div>

                <div className="rmh-money">
                  <div>
                    <span>Ücret</span>
                    <strong>
                      {repair.price_type === 'free' ? <em className="ok">Ücretsiz</em>
                        : repair.price_type === 'pending' ? <em className="warn">Hesaplanacak</em>
                          : total > 0 ? tl(total) : '—'}
                    </strong>
                  </div>
                  <div>
                    <span>Alınan</span>
                    <strong>{collected > 0 ? tl(collected) : '—'}</strong>
                  </div>
                  <div>
                    <span>Kalan</span>
                    <strong>
                      {repair.price_type !== 'normal' || total <= 0 ? '—'
                        : remaining <= 0.01 ? <em className="ok">Ödendi</em>
                          : <em className="due">{tl(remaining)}</em>}
                    </strong>
                  </div>
                </div>

                <div className="rmh-card-foot">
                  <span>{shortDate(repair.created_at)}</span>
                  {isAdmin && effectiveStoreId && <span>· {getStoreName(effectiveStoreId)}</span>}
                  {repair.repair_point_name && <span className="rmh-foot-point"><MapPin size={11} /> {repair.repair_point_name}</span>}
                  {deadline && repair.status !== 'delivered' && (
                    <span className={`rmh-deadline${isOverdue ? ' is-overdue' : isUrgent ? ' is-urgent' : ''}`}>
                      {isOverdue ? '⚠ ' : isUrgent ? '⏰ ' : 'Son: '}{shortDate(repair.deadline)}
                    </span>
                  )}
                </div>
              </button>
            );
          })}
          {repairs.length > visibleCount && (
            <button type="button" className="rmh-more" onClick={() => setVisibleCount(c => c + PAGE_SIZE)}>
              Daha fazla göster ({repairs.length - visibleCount} kayıt kaldı)
            </button>
          )}
        </div>
      )}

      {/* Yeni kayıt */}
      <button type="button" className="rmh-fab" onClick={onNewRepair} aria-label="Yeni tamirat kaydı">
        <Plus size={24} />
      </button>
    </div>
  );
};

export default RepairsMobileHome;
